import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export type MapMarker = {
  lat: number;
  lng: number;
  label?: string;
  color?: string;
  accuracy_m?: number | null;
};

type Props = {
  center: { lat: number; lng: number };
  zoom?: number;
  /** Office pin + geofence circle. */
  office?: { lat: number; lng: number; radius_m: number } | null;
  markers?: MapMarker[];
  /** When set, clicking the map (or dragging the office pin) reports a new position. */
  onPick?: (p: { lat: number; lng: number }) => void;
  height?: number;
};

/**
 * Leaflet over OpenStreetMap tiles (no API key). Circle markers are used instead of image
 * markers so no icon assets need bundling.
 */
export function MapView({ center, zoom = 16, office, markers = [], onPick, height = 280 }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const officeLayer = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!el.current || map.current) return;
    const m = L.map(el.current, { zoomControl: true, attributionControl: true }).setView(
      [center.lat, center.lng],
      zoom,
    );
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(m);
    layer.current = L.layerGroup().addTo(m);
    officeLayer.current = L.layerGroup().addTo(m);
    map.current = m;
    return () => {
      m.remove();
      map.current = null;
    };
    // Mount once; later prop changes are applied by the effects below.
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (onPick) {
      const h = (e: L.LeafletMouseEvent) =>
        onPick({ lat: round(e.latlng.lat), lng: round(e.latlng.lng) });
      m.on('click', h);
      return () => {
        m.off('click', h);
      };
    }
    return undefined;
  }, [onPick]);

  useEffect(() => {
    const g = officeLayer.current;
    if (!g) return;
    g.clearLayers();
    if (office) {
      L.circle([office.lat, office.lng], {
        radius: office.radius_m,
        color: '#0f4c81',
        weight: 1,
        fillOpacity: 0.08,
      }).addTo(g);
      const pin = L.circleMarker([office.lat, office.lng], {
        radius: 8,
        color: '#0f4c81',
        fillColor: '#0f4c81',
        fillOpacity: 1,
      })
        .bindTooltip('Office')
        .addTo(g);
      if (onPick) {
        // Drag-to-move via mouse events on the pin (circleMarker has no built-in dragging).
        let dragging = false;
        const m = map.current!;
        pin.on('mousedown', () => {
          dragging = true;
          m.dragging.disable();
        });
        m.on('mousemove', (e: L.LeafletMouseEvent) => {
          if (dragging) pin.setLatLng(e.latlng);
        });
        m.on('mouseup', (e: L.LeafletMouseEvent) => {
          if (!dragging) return;
          dragging = false;
          m.dragging.enable();
          onPick({ lat: round(e.latlng.lat), lng: round(e.latlng.lng) });
        });
      }
    }
  }, [office?.lat, office?.lng, office?.radius_m, onPick, office]);

  useEffect(() => {
    const g = layer.current;
    if (!g) return;
    g.clearLayers();
    for (const mk of markers) {
      const color = mk.color ?? '#b91c1c';
      const c = L.circleMarker([mk.lat, mk.lng], {
        radius: 6,
        color,
        fillColor: color,
        fillOpacity: 0.9,
      }).addTo(g);
      if (mk.label) c.bindTooltip(mk.label);
      if (mk.accuracy_m)
        L.circle([mk.lat, mk.lng], {
          radius: mk.accuracy_m,
          color,
          weight: 1,
          fillOpacity: 0.05,
          dashArray: '4 4',
        }).addTo(g);
    }
  }, [markers]);

  useEffect(() => {
    map.current?.setView([center.lat, center.lng]);
  }, [center.lat, center.lng]);

  // Leaflet measures its container on mount; make sure a late layout does not leave grey tiles.
  useEffect(() => {
    const t = setTimeout(() => map.current?.invalidateSize(), 50);
    return () => clearTimeout(t);
  });

  return <div ref={el} className="map" style={{ height }} />;
}

const round = (n: number) => Math.round(n * 1e6) / 1e6;
